import React from 'react';

import {arrayLikeToArray} from '../lib/helpers.js';
import {Richtext} from '../common/Richtext/Richtext.jsx';

const OPTION_SELECT = 'select';
const OPTION_BLANK = 'blank';
const OPTION_REMOVE = 'remove';
const metaOptions = new Set([OPTION_SELECT, OPTION_BLANK, OPTION_REMOVE]);

let uid = 1;

function stripHtmlTags(s) {
    return String(s).replace(/(<([^>]+)>)/gi, '');
}

function encodeHtml(s) {
    return String(s).replace(/[&'"<>]/g, function(c) {
        return '&#' + c.charCodeAt(0) + ';';
    });
}

function decodeHtml(s) {
    const e = document.createElement('div');
    return String(s).replace(/&[#0-9a-z]+;/gi, function(c) {
        e.innerHTML = c;
        return e.textContent;
    });
}

function parseTimeSelectString(html) {
    const r = [];
    const div = document.createElement('div');
    div.innerHTML = html;
    for (const option of div.querySelectorAll('option')) {
        r.push({
            value: option.value,
            time: option.getAttribute('time'),
            html: option.innerHTML,
            selected: option.selected
        });
    }
    return r;
}

function getHourMinute(dateObj) {
    return moment(dateObj).format('HH:mm');
}

function getDateStamp(dateObj) {
    return moment(dateObj).format('YYYY-MM-DD');
}

function getDateTimeStamp(dateObj) {
    return moment(dateObj).format('YYYY-MM-DD HH:mm:ss');
}

function calculateTimeBeforeOrAfter(timeType, timeRange, useDateTime) {
    var date;
    if (timeRange === undefined || timeType === undefined) {
        return '1970-01-01 00:00:00';
    }

    if (useDateTime) { // new
        date = new Date(timeRange);
    } else { // old, before fullDateTime
        date = new Date('November 13, 2015 ' + timeRange);
    }

    if (timeType === 'after') {
        date.setMinutes(date.getMinutes() + 120);
    } else {
        date.setMinutes(date.getMinutes() - 120);
    }

    return (useDateTime ? getDateStamp(date) + ' ' : '') + getHourMinute(date);
}

function formAvailableTimeRangeOptions(timeRangeStart, timeRangeEnd, timeHashOnly) {
    var options = [];
    var timeHash = {};
    var interval = 30; // adjusted increment to 30 minutes from 15
    var stt;
    var endt;
    var hasDateTimeFormat = ! isNaN(new Date(timeRangeStart).getTime());
    if (hasDateTimeFormat) {
        stt = new Date(timeRangeStart); // need to come up with what to do if start is greater than end
        endt = new Date(timeRangeEnd);
    } else { // old, before fullDateTime
        stt = new Date('November 13, 2013 ' + timeRangeStart);
        // // work-around for edge cases where start time is at midnight and trying to set
        if (timeRangeStart > timeRangeEnd) {
            stt = new Date('November 12, 2013 ' + timeRangeStart);
        }
        endt = new Date('November 13, 2013 ' + timeRangeEnd); // old, before fullDateTime
    }
    stt.setMinutes(stt.getMinutes() + interval);

    var stts = stt.getTime();
    var endts = endt.getTime();
    for (var i = 0; stts < endts && (hasDateTimeFormat || i < 100); i++) {
        var date = g_locale.formatDateTime(stt);
        var time = getHourMinute(stt);
        if (hasDateTimeFormat) { // new
            options.push({
                time: getDateTimeStamp(stt),
                html: encodeHtml(date),
                selected: false
            });
            timeHash[date] = 1;
        } else { // old, before fullDateTime
            options.push({
                time,
                html: encodeHtml(converToAmPm(time)),
                selected: false
            });
            timeHash[date + ' ' + time] = 1; // old, before fullDateTime
        }
        stt.setMinutes(stt.getMinutes() + interval);
        stts = stt.getTime();
    }
    return timeHashOnly ? timeHash : options;
}

function getSelectedOption(options) {
    let r = null;
    for (const o of options) {
        if (o.selected) {
            r = o;
        }
    }
    return r;
}

function eventViewTime(item) {
    const timeAttr = item.timeAttr || '';
    switch (timeAttr) {
        case '':
        case 'blank': {
            return (
                <div className="tl-date-blank-wrap">
                    <div className="tl-date-blank-circle"></div>
                </div>
            );
        }
    }
    return g_locale.formatDateTime(timeAttr);
}

function itemIsStep(item) {
    return ! (item.isAdd || item.isDefault);
}

function updateValidations(timeline, l10n) {
    // Find how many times each time is selected.
    const timeAttrs = new Map();
    for (const item of timeline) {
        if (! itemIsStep(item)) {
            continue;
        }
        if (! item.timeAttr || metaOptions.has(item.timeAttr)) {
            continue;
        }
        timeAttrs.set(item.timeAttr, (timeAttrs.get(item.timeAttr) || 0) + 1);
    }

    return timeline.map(item => {
        if (! itemIsStep(item)) {
            return item;
        }

        const errors = [];

        // If this time is used more then once, add error.
        if ((timeAttrs.get(item.timeAttr) || 0) > 1) {
            errors.push(l10n.timeSelectDuplicate);
        }

        return {
            ...item,
            _errors: errors.length ? errors : null
        };
    });
}

export function timelineHasErrors(timeline) {
    for (const item of timeline) {
        if (item._errors) {
            return true;
        }
    }
    return false;
}

export function timelineGetErrors(timeline) {
    const errors = new Set();
    for (const item of timeline) {
        if (item._errors) {
            for (const error of item._errors) {
                errors.add(error);
            }
        }
    }
    return [...errors];
}

export function timelineDecode(json) {
    let timeline = JSON.parse(json);

    // Backwards compat: Could be object with numeric keys.
    timeline = Array.isArray(timeline) ? timeline : arrayLikeToArray(timeline);

    for (const item of timeline) {
        // Backwards compat: Could be an HTML select string.
        if (typeof item.options === 'string') {
            item.options = parseTimeSelectString(item.options);
        }
        // Backwards compat: Could be object with numeric keys.
        else if (item.options && ! Array.isArray(item.options)) {
            item.options = arrayLikeToArray(item.options);
        }

        // Add unique ID to lists for React key.
        item._id = uid++;
    }

    return timeline;
}

export function timelineEncode(timeline) {
    // Remove any properties with _ prefix.
    return JSON.stringify(timeline, (k, v) => k[0] === '_' ? undefined : v);
}

export function TimelineItemDefault({
    item,
    onChange,
    viewOnly = false
}) {
    const title = viewOnly ?
        (
            <div className='tl-title' dangerouslySetInnerHTML={{__html: item.title}} />
        ) : (
            <div className='tl-title'>
                <Richtext
                    height='4em'
                    className='timeLine-title'
                    aria-label={getJsText('vp_todo_guest_expect')}
                    placeholder={getJsText('vp_todo_guest_expect')}
                    html={item.title || ''}
                    onChange={html => {
                        if (onChange) {
                            onChange({...item, title: html});
                        }
                    }}
                />
            </div>
        );
    const desc = viewOnly ?
        (
            <div className='tl-desc' dangerouslySetInnerHTML={{__html: item.desc}} />
        ) : (
            <div className='tl-desc'>
                <Richtext
                    className='timeLine-desc'
                    aria-label={getJsText('Describe in detail')}
                    placeholder={getJsText('Describe in detail')}
                    html={item.desc || ''}
                    onChange={html => {
                        if (onChange) {
                            onChange({...item, desc: html});
                        }
                    }}
                />
            </div>
        );

    return (
        <li className={`tl-item tl-valid-time ${item.defaultClass || ''} ${item.class || ''}`}>
            <div className={`tl-date ${viewOnly ? 'colored' : ''}`}>
                <time dateTime={item.timeAttr}>
                    {item.time}
                </time>
            </div>
            <div className='tl-content'>
                {title}
                {desc}
            </div>
        </li>
    );
}

export function TimelineItemAdd({
    item,
    onAdd
}) {
    return (
        <li className='tl-item add-circle'>
            <div className={`tl-date tl-add ${item.class}`}>
                <button type='button' className='tl-add-button'>
                    <span className='tl-circle' onClick={() => {
                        if (onAdd) {
                            onAdd();
                        }
                    }}></span>
                    <span className='add-label'>{getJsText('add a step')}</span>
                </button>
            </div>
        </li>
    );
}

export function TimelineItemStep({
    item,
    onChange,
    viewOnly = false
}) {
    const optionsByValue = new Map();
    let optionSelected = undefined;
    for (const o of item.options) {
        const v = o.time || o.value;
        optionsByValue.set(v, o);
        optionSelected = o.selected ? v : optionSelected;
    }

    const time = viewOnly ?
        (
            <time dateTime={item.timeAttr}>
                {eventViewTime(item)}
            </time>
        ) : (
            <select
                time={item.timeAttr}
                value={optionSelected}
                className={item._errors ? 'warning-border' : ''}
                title={item._errors ? item._errors.join('\n') : null}
                onChange={e => {
                    if (onChange) {
                        const option = optionsByValue.get(e.target.value);
                        const changed = {
                            ...item,
                            timeAttr: option.time || '',
                            validTimeClass: option.time ? 'tl-valid-time' : '',
                            options: item.options.map(o => ({
                                ...o,
                                selected: o === option
                            }))
                        };
                        onChange(changed);
                    }
                }}
            >{[...optionsByValue.entries()].map(([v, o]) => (
                <option
                    key={v}
                    value={v}
                >{decodeHtml(stripHtmlTags(o.html))}</option>
            ))}</select>
        );
    const title = viewOnly ?
        (
            <div className='tl-title' dangerouslySetInnerHTML={{__html: item.title}} />
        ) : (
            <div className='tl-title'>
                <Richtext
                    height='4em'
                    className='timeLine-title'
                    aria-label={getJsText('vp_todo_guest_expect')}
                    placeholder={getJsText('vp_todo_guest_expect')}
                    html={item.title || ''}
                    onChange={html => {
                        if (onChange) {
                            onChange({...item, title: html});
                        }
                    }}
                />
                <button
                    type='button'
                    className='tl-remove'
                    onClick={() => {
                        if (onChange) {
                            onChange(null);
                        }
                    }}
                ></button>
            </div>
        );
    const desc = viewOnly ?
        (
            <div className='tl-desc' dangerouslySetInnerHTML={{__html: item.desc}} />
        ) : (
            <div className='tl-desc'>
                <Richtext
                    className='timeLine-desc'
                    aria-label={getJsText('Describe in detail')}
                    placeholder={getJsText('Describe in detail')}
                    html={item.desc || ''}
                    onChange={html => {
                        if (onChange) {
                            onChange({...item, desc: html});
                        }
                    }}
                />
            </div>
        );

    return (
        <li
            className={`tl-item new-added ${item.validTimeClass} ${item.class || ''}`}
            pos={item.pos}
            base={item.base}
        >
            <div
                className={`tl-date ${viewOnly ? 'colored' : ''}`}
            >{time}</div>
            <div className='tl-content'>
                {title}
                {desc}
            </div>
        </li>
    );
}

export function Timeline({
    l10n,
    timeline,
    onChange,
    viewOnly = false
}) {
    // console.log('Timeline:', timeline);
    const optionsFirst = [
        {
            value: OPTION_SELECT,
            html: encodeHtml(l10n.time),
            selected: true
        },
        {
            value: OPTION_BLANK,
            html: encodeHtml(l10n.blank),
            selected: false
        }
    ];
    const optionsLast = [
        {
            value: OPTION_REMOVE,
            html: encodeHtml(l10n.remove),
            selected: false
        }
    ];

    const defaults = timeline.filter(o => o.isDefault);

    const useDateTime = defaults.length && (
        defaults[0].useDateTime ||
        ! isNaN((new Date(defaults[0].timeAttr)).getTime())
    );

    return (
        <div className={`timeLineRoot tl-mode-${viewOnly ? 'view' : 'edit'}`}>
            <ul className='timeline'>{timeline.map((item, itemI) => {
                if (item.isDefault) {
                    return (
                        <TimelineItemDefault
                            key={item._id}
                            item={item}
                            viewOnly={viewOnly}
                            onChange={item => {
                                if (! onChange) {
                                    return;
                                }

                                const tl = timeline.slice();
                                tl[itemI] = item;
                                onChange(tl);
                            }}
                        />
                    );
                }
                if (item.isAdd) {
                    if (viewOnly) {
                        return null;
                    }
                    return (
                        <TimelineItemAdd
                            key={item._id}
                            item={item}
                            viewOnly={viewOnly}
                            onAdd={() => {
                                if (! onChange) {
                                    return;
                                }

                                let range = null;
                                let pos = '';
                                let base = '';
                                let insert = -1;
                                if (item.class === 'above') {
                                    range = [
                                        calculateTimeBeforeOrAfter(
                                            'before',
                                            defaults[0].time,
                                            useDateTime
                                        ),
                                        defaults[0].time
                                    ];
                                    insert = itemI;
                                    pos = 'above';
                                    base = 'aboveAdd';
                                }
                                else if (item.class === 'middle') {
                                    range = [
                                        defaults[0].time,
                                        defaults[1].time
                                    ];
                                    insert = itemI;
                                    pos = 'below';
                                    base = 'middleAdd';
                                }
                                else {
                                    return;
                                }

                                const tl = timeline.slice();
                                tl.splice(insert, 0, {
                                    timeAttr: '',
                                    options: [
                                        ...optionsFirst,
                                        ...formAvailableTimeRangeOptions(
                                            range[0],
                                            range[1]
                                        ),
                                        ...optionsLast
                                    ],
                                    title: '',
                                    desc: '',
                                    pos,
                                    base,
                                    validTimeClass: '',
                                    useDateTime,
                                    _id: uid++
                                });
                                onChange(tl);
                            }}
                        />
                    );
                }
                return (
                    <TimelineItemStep
                        key={item._id}
                        item={item}
                        viewOnly={viewOnly}
                        onChange={item => {
                            if (! onChange) {
                                return;
                            }

                            const selected = item ?
                                getSelectedOption(item.options) :
                                null;
                            if (selected?.value === 'remove') {
                                item = null;
                            }

                            const tl = timeline.slice();
                            if (item) {
                                tl[itemI] = item;
                            }
                            else {
                                tl.splice(itemI, 1);
                            }
                            onChange(updateValidations(tl, l10n));
                        }}
                    />
                );
            })}</ul>
        </div>
    );
}
